import { Loading } from "../app/Status";
import { PageHeader } from "../app/PageHeader";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { ROLE_LABELS, type BuildingSummary } from "@klantpunt/shared";
import { IconBell, IconLogout, IconSettings, IconSwitch } from "../app/icons";
import { Avatar, ThemeSwitch } from "../app/ProfileMenu";
import { useLogout } from "../app/useLogout";
import { PushSettings } from "../features/push/PushSettings";
import { api } from "../lib/api";
import { can, useMe } from "../lib/auth";
import { useTheme } from "../lib/theme";

/** Profiel en instellingen: account, weergave, notificaties per gebouw, pushmeldingen, organisatie, uitloggen. */
export function ProfilePage() {
  const { data: me } = useMe();
  const [theme, setTheme] = useTheme();
  const logout = useLogout();
  const buildings = useQuery({
    queryKey: ["buildings", me?.org?.id],
    queryFn: () => api<BuildingSummary[]>("/buildings"),
  });
  const manager = can(me, "users.manage") || me?.role === "owner";
  if (!me) return null;

  return (
    <div className="page narrow profile">
      <PageHeader label="Account" title="Profiel en instellingen" />

      <section className="panel" aria-labelledby="p-account">
        <h2 id="p-account">Account</h2>
        <div className="profile-head">
          <Avatar user={me.user} size="lg" />
          <span className="pm-who">
            <strong>{me.user.name ?? me.user.email}</strong>
            {me.user.name && <span className="muted">{me.user.email}</span>}
          </span>
        </div>
      </section>

      <section className="panel" aria-labelledby="p-weergave">
        <h2 id="p-weergave">Weergave</h2>
        <ThemeSwitch theme={theme} setTheme={setTheme} />
        <p className="muted small">
          Systeem volgt de instelling van je apparaat. Je keuze wordt op dit apparaat bewaard.
        </p>
      </section>

      <section className="panel" aria-labelledby="p-notificaties">
        <h2 id="p-notificaties">Notificaties per e-mail</h2>
        <p className="muted small">
          Per gebouw kies je welke mails je krijgt. Standaard alleen belangrijke zaken; notificaties worden een paar
          minuten gebundeld, spoed komt direct.
        </p>
        {buildings.isPending ? (
          <Loading />
        ) : buildings.data?.length ? (
          <ul className="list">
            {buildings.data.map((b) => (
              <li key={b.id}>
                <span className="code">{b.code}</span>
                <span className="grow">{b.name}</span>
                <Link
                  className="btn small"
                  aria-label={`Notificaties voor ${b.name}`}
                  to={`/gebouwen/${b.id}/gebouw?tab=notificaties`}
                >
                  <IconBell size={18} aria-hidden="true" />
                  Instellen
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted">Je hebt nog geen gebouwen.</p>
        )}
      </section>

      <PushSettings />

      {me.org && me.role && (
        <section className="panel" aria-labelledby="p-org">
          <h2 id="p-org">Organisatie</h2>
          <div className="profile-row">
            <span className="grow">
              <strong>{me.org.name}</strong>
              <br />
              <span className="muted small">Jouw rol: {ROLE_LABELS[me.role]}</span>
            </span>
            {me.orgs.length > 1 && (
              <Link to="/organisatie" className="btn">
                <IconSwitch size={18} aria-hidden="true" />
                Wisselen
              </Link>
            )}
          </div>
          {manager && (
            <p>
              <Link to="/gebruikers" className="btn">
                <IconSettings size={18} aria-hidden="true" />
                Organisatie-instellingen
              </Link>
              <span className="muted small block">Gebruikers, logo en huisstijl, abonnement.</span>
            </p>
          )}
        </section>
      )}

      <section className="panel" aria-labelledby="p-sessie">
        <h2 id="p-sessie">Sessie</h2>
        <div className="profile-row">
          <span className="grow muted small">
            Gegevens op dit apparaat worden gewist; wachtende wijzigingen blijven bewaard.
          </span>
          <button type="button" className="btn danger-outline" onClick={() => void logout()}>
            <IconLogout size={18} aria-hidden="true" />
            Uitloggen
          </button>
        </div>
      </section>
    </div>
  );
}
